import React, { useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import {
  createTaxLedgerAccount,
  TaxLedgerSlots,
  TdsLedgerAccount,
} from "../../../../services/api";

/**
 * The option value that means "make a new account" rather than naming one.
 *
 * Never sent anywhere and never stored on the draft: picking it opens the
 * creator and leaves the select on whatever it said before, so backing out of
 * the idea does not first move the rate somewhere. Prefixed past any chance of
 * colliding with a Zoho account id, which are long digit strings.
 */
export const NEW_ACCOUNT = "__new_account__";

/** Which slot this is, and therefore what kind of account it can point at. */
export type LedgerSlotName = "payable" | "receivable";

/**
 * The account type each slot takes. THE SAME PAIR FOR BOTH TAXES — what inverts
 * between TDS and TCS is which payload FIELD the account travels in
 * (`tax_account_id` is the receivable on a TDS rate and the payable on a TCS
 * one), and that is the calling dialog's business, not this component's.
 *
 * Mirrors SyncController::TDS_LEDGER_SLOTS / TCS_LEDGER_SLOTS, whose
 * account_type column says the same thing. The backend re-checks it; this copy
 * exists so the create call can say which type it wants.
 */
const SLOT_ACCOUNT_TYPE: Record<
  LedgerSlotName,
  "other_current_liability" | "other_current_asset"
> = {
  payable: "other_current_liability",
  receivable: "other_current_asset",
};

interface LedgerSlotSelectProps {
  label: string;
  slot: LedgerSlotName;
  /** Which tax's slot offer to read back after a create. */
  kind: "tds" | "tcs";
  accounts: TdsLedgerAccount[];
  value: string;
  onChange: (accountId: string) => void;
  /**
   * The chart of accounts changed underneath — "+ New account" wrote to it. The
   * dialog owns the list both slots read from, so the fresh offer goes up.
   */
  onSlotsRefreshed: (slots: TaxLedgerSlots) => void;
}

/**
 * One ledger slot on a rate form, with Zoho's own "+ New Account" answer inside
 * the dropdown.
 *
 * ONE COMPONENT FOR BOTH DIALOGS AND BOTH SLOTS. Zoho's TDS and TCS dialogs are
 * the same dialog, and the four selects across them differ only in their label,
 * their list, and which draft key they write. Duplicating the creator four times
 * would be four places for the account type to drift out of step with the slot
 * it was opened from.
 *
 * The creator WRITES TO THE ORG'S REAL CHART OF ACCOUNTS on its own button,
 * before the rate is saved — the account has to exist for the rate to point at
 * it. Gentler than a rate write: Zoho keeps account names unique, so a
 * double-press comes back refused rather than making a twin.
 */
export const LedgerSlotSelect: React.FC<LedgerSlotSelectProps> = ({
  label,
  slot,
  kind,
  accounts,
  value,
  onChange,
  onSlotsRefreshed,
}) => {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accountType = SLOT_ACCOUNT_TYPE[slot];

  /**
   * Make the account, then stand this slot on it.
   *
   * The fresh offer goes up to the dialog — both slots read from it — and this
   * one selects the new id, so the operator lands back on a form already saying
   * what they came to say. When Zoho created the account but did not hand back
   * an id there is nothing to select; the backend refuses that case outright
   * rather than inviting a second copy.
   */
  const add = async () => {
    const wanted = name.trim();
    if (wanted === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await createTaxLedgerAccount({
        account_name: wanted,
        account_type: accountType,
        kind,
      });
      onSlotsRefreshed(result.data.slots);
      if (result.data.account.account_id !== "") {
        onChange(result.data.account.account_id);
      }
      setCreating(false);
      setName("");
    } catch (err) {
      // Zoho's own refusal, or our sentence about a taken name — either is
      // worth more on screen than anything written over it.
      setError(
        err instanceof Error ? err.message : "Could not add the account.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex grow flex-col items-start gap-2">
      <Select
        className="w-full"
        label={label}
        placeholder="Zoho picks"
        helpText={accountType.replace(/_/g, " ")}
        value={value}
        onValueChange={(picked: string) =>
          // "+ New account…" is a verb in a list of nouns: it opens the creator
          // and the select stays where it was.
          picked === NEW_ACCOUNT ? setCreating(true) : onChange(picked)
        }
      >
        {accounts.map((a) => (
          <Select.Item key={a.account_id} value={a.account_id}>
            {a.account_name}
          </Select.Item>
        ))}
        {/* Last, where Zoho's own dialog keeps it. The answer to "the account I
            want is not in this list" lives in the list that raised it. */}
        <Select.Item value={NEW_ACCOUNT}>+ New account…</Select.Item>
      </Select>

      {creating && (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-dashed border-neutral-border p-3">
          <TextField
            className="w-full"
            label={`New account for ${label}`}
            helpText={`Created in the chart of accounts as an ${accountType.replace(/_/g, " ")}.`}
          >
            <TextField.Input
              autoFocus
              placeholder={
                slot === "payable"
                  ? `e.g. ${kind.toUpperCase()} Payable - Contractors`
                  : `e.g. ${kind.toUpperCase()} Receivable - Contractors`
              }
              maxLength={255}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              // The name is the whole form, so typing should be able to finish
              // the job — but only submit this one: without the guard the Enter
              // would reach the dialog's own handling.
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
          </TextField>

          {error && (
            <span className="text-caption font-caption text-red-600">
              {error}
            </span>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="small"
              variant="brand-secondary"
              onClick={add}
              disabled={busy || name.trim() === ""}
            >
              {busy ? "Adding…" : "Add to chart of accounts"}
            </Button>
            <Button
              size="small"
              variant="neutral-tertiary"
              onClick={() => {
                setCreating(false);
                setError(null);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerSlotSelect;
